import {
  Label,
  OperationCreate,
  OperationPayloadSwitchPage,
  OperationType,
  PaginationResponse,
  Pagination,
} from '@ekkogmbh/apisdk';
import { Grid } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { UploadStep } from '../../Common/Components/CSVImportStepper/UploadStep';
import { spacer } from '../../Common/Components/Forms/Spacer';
import { StyledSelectField } from '../../Common/Components/Forms/StyledSelectField';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import PaginatedSelect from 'src/Common/Components/Forms/PaginatedSelect';
import { usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import StyledDropzone from 'src/Common/Components/Forms/StyledDropzone';

interface Props {
  operations: OperationCreate[];
  file?: File;
  type: OperationType;
  onChange: (operations: OperationCreate[]) => void;
  onChangeFile: (file?: File) => void;
  fetchLabels: (pagination: Pagination) => Promise<PaginationResponse<Label>>;
}

export const OperationForm = ({
  fetchLabels,
  operations,
  file,
  type,
  onChange,
  onChangeFile,
}: Props): React.JSX.Element => {
  const { enableNext } = usePanel();
  const [isFileDropped, setFileDropped] = useState(false);
  const [coordinate, setCoordinate] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(0);

  const selectedLabels = useMemo(
    () =>
      operations.map((o) => {
        const { labelId, technology } = o.payload as OperationPayloadSwitchPage;
        return { id: labelId, technology };
      }),
    [operations],
  );

  useEffect(() => {
    if (operations.length > 0) {
      setPageNumber((operations[0].payload as OperationPayloadSwitchPage).pageNumber ?? 0);
    }
  }, [operations]);

  /** reactive panel buttons */
  useEffect(() => {
    switch (type) {
      case OperationType.SWITCH_PAGE:
        enableNext(true);
        break;
      case OperationType.COMPARTMENT_UPDATE:
        enableNext(file !== undefined);
        break;
      default:
        enableNext(false);
    }
  }, [type, file]);

  const stepCompartmentImport = useMemo(
    () => (
      <Grid container item spacing={2} alignItems={'stretch'}>
        <Grid item xs={4}>
          <StyledSelectField native disabled value="compartment-update-import" onChange={() => undefined} label="Type">
            <option key={0} value="compartment-update-import">
              Compartment-Import
            </option>
          </StyledSelectField>
        </Grid>

        {spacer(8)}

        {isFileDropped && (
          <Grid item xs={12}>
            <div>
              <UploadStep file={file} loading={false} />
            </div>
          </Grid>
        )}

        {!isFileDropped && (
          <Grid item xs={12}>
            <section style={{ width: '100%' }}>
              <StyledDropzone
                instructions={'Drop or select a compartment import file'}
                multiple={false}
                onDrop={(f) => {
                  onChangeFile(f.length > 0 ? f[0] : undefined);
                  setFileDropped(f.length > 0);
                }}
              />
            </section>
          </Grid>
        )}
      </Grid>
    ),
    [isFileDropped, file, setFileDropped, onChangeFile],
  );

  const stepSwitchPage = useMemo(
    () => (
      <Grid container item spacing={2} alignItems={'stretch'}>
        <Grid item xs={4}>
          <StyledSelectField native disabled value="label-select" onChange={() => undefined} label="Type">
            <option key={0} value="label-select">
              Label-Select
            </option>
          </StyledSelectField>
        </Grid>

        {spacer(8)}

        <Grid container item spacing={2}>
          <Grid item xs={3}>
            <CoordinateInput
              label={'Filter By Coordinate'}
              trailingDelimiter={false}
              onChange={(c) => setCoordinate(c)}
            />
          </Grid>

          <Grid item xs={1}>
            <StyledTextField
              type={'number'}
              value={pageNumber}
              label={'Page Number'}
              onChange={(e) => {
                const inputNumber = parseInt(e.target.value);
                const value = isNaN(inputNumber) ? 0 : Math.max(0, inputNumber);
                setPageNumber(value);
              }}
            />
          </Grid>
        </Grid>
        <Grid container item>
          <PaginatedSelect
            multiple
            value={selectedLabels}
            label={'Labels'}
            fetchPaginatedItems={fetchLabels}
            onChange={(value) => {
              const labels = value as Label[];
              const labelOperations: OperationCreate[] = labels.map((l) => ({
                identifier: l.identifierValue,
                type: OperationType.SWITCH_PAGE,
                payload: {
                  labelId: l.id,
                  technology: l.technology,
                  pageNumber,
                },
              }));
              onChange(labelOperations);
            }}
            itemId={(label) => label.id + '::' + label.technology}
            itemRender={(label) => label.id + ' (' + label.technology + ')'}
            filtering={coordinate ? { filter: { identifier: coordinate } } : undefined}
          />
        </Grid>
      </Grid>
    ),
    [coordinate, pageNumber, selectedLabels, fetchLabels],
  );

  const form = useMemo(
    () => (type == OperationType.COMPARTMENT_UPDATE ? stepCompartmentImport : stepSwitchPage),
    [stepCompartmentImport, stepSwitchPage, type],
  );

  return (
    <Grid container spacing={2} alignItems={'stretch'}>
      {form}
    </Grid>
  );
};
